import { usePhoneStore } from '@hit/react-jssip';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import {
  type ExtensionIndicator,
  deriveExtensionIndicator,
  indicatorColors,
} from './model';

export function ExtensionStatusBar({
  number,
  status = 'loading',
}: {
  number?: string;
  status?: ExtensionIndicator;
}) {
  const { t } = useTranslation('extension-status');
  const label = t(status);
  return (
    <div
      className="flex h-8 min-h-8 items-center gap-2 border-b border-[#c8c8c8] bg-[#fafafa] pl-5 pr-2"
      role="status"
      aria-label={`${number ?? t('loading')}: ${label}`}
    >
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              role="group"
              aria-label={`${number ?? t('loading')}: ${label}`}
              className="-m-1.5 inline-flex min-w-0 items-center gap-2 rounded p-1.5"
            >
              <span
                aria-hidden="true"
                className={`aspect-square h-2 rounded-full ${indicatorColors[status]}`}
              />
              <span className="select-none truncate text-xs font-semibold text-[#5a5a5a]">
                {number ?? '----'}
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="start"
            collisionPadding={8}
            avoidCollisions={false}
            className="relative select-none overflow-visible border border-[#dedede] bg-[#f2f2f2] text-[#404040] shadow dark:bg-[#f2f2f2] dark:text-[#404040]"
          >
            {label}
            <svg
              aria-hidden="true"
              width="10"
              height="5"
              viewBox="0 0 10 5"
              className="absolute -top-[5px] left-[4px] overflow-visible"
            >
              <path d="M0 5L5 0L10 5" fill="#f2f2f2" stroke="#dedede" />
            </svg>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default function ExtensionStatus({ number }: { number: string }) {
  const phone = usePhoneStore((state) => state.phone);
  const transport = usePhoneStore((state) => state.wsConnectionState);
  const registration = usePhoneStore((state) => state.extensionState);
  const [observed, setObserved] = useState<{
    phone: typeof phone;
    registration: string;
  }>();
  useEffect(() => {
    if (!phone) return;
    // The SDK retains registration values across reconnects. A fresh transport
    // starts a new registration attempt; UA events confirm its actual result.
    const pending = () => setObserved({ phone, registration: 'initial' });
    const registered = () => setObserved({ phone, registration: 'registered' });
    const unregistered = () =>
      setObserved({ phone, registration: 'unregistered' });
    const failed = () =>
      setObserved({ phone, registration: 'registrationFailed' });
    phone.on('connecting', pending);
    phone.on('connected', pending);
    phone.on('registered', registered);
    phone.on('unregistered', unregistered);
    phone.on('registrationFailed', failed);
    return () => {
      phone.removeListener('connecting', pending);
      phone.removeListener('connected', pending);
      phone.removeListener('registered', registered);
      phone.removeListener('unregistered', unregistered);
      phone.removeListener('registrationFailed', failed);
    };
  }, [phone]);
  return (
    <ExtensionStatusBar
      number={number}
      status={deriveExtensionIndicator({
        transport,
        registration:
          observed?.phone === phone ? observed.registration : registration,
        isRegistered: phone?.isRegistered() ?? false,
      })}
    />
  );
}
